// ============================================================================
// @torre/expenses — Filtros de Pago de plataformas
// ============================================================================
// Rango de fechas (meses), cliente/tenant, tipo de plataforma (categoría),
// tipo de suscripción (modelo de cobro), licenciamiento y estado. La lectura y
// escritura en la URL vive en v5 (lib/platformFiltersUrl.ts). Lógica pura.
// ============================================================================

import type { Platform, PlatformReport } from "./index";
import { billingModelOf, type PlatformBillingModel } from "./platformBilling";
import type { PlatformLicensing } from "./platformDiligence";
import { PLATFORM_KINDS, platformKindOf, type PlatformKind } from "./platformKind";

export const PLATFORM_STATUS_FILTERS = ["todas", "activas", "inactivas"] as const;
export type PlatformStatusFilter = (typeof PLATFORM_STATUS_FILTERS)[number];

/** Máximo de meses que se piden al reporte de lo pagado. */
export const PLATFORM_FILTER_MAX_MONTHS = 36;

export interface PlatformFilters {
  /** YYYY-MM inclusive. */
  from: string;
  /** YYYY-MM inclusive. */
  to: string;
  /** Tenant o cliente (`tenants` de la plataforma); vacío = todos. */
  client: string;
  /** Categoría de gasto (tipo de plataforma); vacío = todas. */
  category: string;
  /** Plataforma (herramienta del equipo) o infraestructura; vacío = ambas. */
  kind: PlatformKind | "";
  /** Modelo de cobro (tipo de suscripción); vacío = todos. */
  model: PlatformBillingModel | "";
  licensing: PlatformLicensing | "";
  status: PlatformStatusFilter;
}

const PERIOD = /^\d{4}-(0[1-9]|1[0-2])$/;

function shift(period: string, delta: number): string {
  const [y, m] = period.split("-").map(Number);
  const d = new Date(Date.UTC(y, m - 1 + delta, 1));
  return d.toISOString().slice(0, 7);
}

function monthsBetween(from: string, to: string): number {
  const [fy, fm] = from.split("-").map(Number);
  const [ty, tm] = to.split("-").map(Number);
  return (ty - fy) * 12 + (tm - fm) + 1;
}

/** Filtros por defecto: los últimos 6 meses, todo lo demás sin filtrar. */
export function defaultPlatformFilters(currentMonth: string): PlatformFilters {
  return { from: shift(currentMonth, -5), to: currentMonth, client: "", category: "", kind: "", model: "", licensing: "", status: "todas" };
}

/**
 * Deja un rango válido: meses bien formados, `from` ≤ `to`, `to` no pasa del
 * mes actual y no más de 36 meses (se recorta `from`).
 */
export function normalizePlatformFilters(f: Partial<PlatformFilters>, currentMonth: string): PlatformFilters {
  const def = defaultPlatformFilters(currentMonth);
  let to = PERIOD.test(String(f.to || "")) ? (f.to as string) : def.to;
  if (to > currentMonth) to = currentMonth;
  let from = PERIOD.test(String(f.from || "")) ? (f.from as string) : shift(to, -5);
  if (from > to) from = to;
  if (monthsBetween(from, to) > PLATFORM_FILTER_MAX_MONTHS) from = shift(to, -(PLATFORM_FILTER_MAX_MONTHS - 1));
  const model = ["asiento", "servicio", "consumo"].includes(String(f.model)) ? (f.model as PlatformBillingModel) : "";
  const licensing = ["uso", "asiento", "ambos", "fijo", "gratis"].includes(String(f.licensing)) ? (f.licensing as PlatformLicensing) : "";
  const status = PLATFORM_STATUS_FILTERS.includes(f.status as PlatformStatusFilter) ? (f.status as PlatformStatusFilter) : "todas";
  const kind = PLATFORM_KINDS.includes(f.kind as PlatformKind) ? (f.kind as PlatformKind) : "";
  return { from, to, client: String(f.client || "").trim(), category: String(f.category || "").trim(), kind, model, licensing, status };
}

/** Plataformas que pasan los filtros. El rango excluye las dadas de alta después de `to`. */
export function filterPlatforms<T extends Platform>(platforms: readonly T[], f: PlatformFilters): T[] {
  return platforms.filter((p) => {
    if (f.status === "activas" && p.active === false) return false;
    if (f.status === "inactivas" && p.active !== false) return false;
    if (f.client && !(p.tenants || []).includes(f.client)) return false;
    if (f.category && p.category !== f.category) return false;
    if (f.kind && platformKindOf(p) !== f.kind) return false;
    if (f.model && billingModelOf(p) !== f.model) return false;
    if (f.licensing && p.licensing !== f.licensing) return false;
    if (p.createdAt && p.createdAt.slice(0, 7) > f.to) return false;
    return true;
  });
}

/** Deja en el reporte solo las plataformas visibles. */
export function filterPlatformReport(report: readonly PlatformReport[], visibleIds: ReadonlySet<string>): PlatformReport[] {
  return report.filter((r) => visibleIds.has(r.platformId));
}

/** Clientes/tenants presentes en el catálogo, ordenados, para el selector. */
export function platformClientOptions(platforms: readonly Platform[]): string[] {
  return Array.from(new Set(platforms.flatMap((p) => p.tenants || []))).sort((a, b) => a.localeCompare(b));
}

/** Categorías presentes en el catálogo, para el selector de tipo de plataforma. */
export function platformCategoryOptions(platforms: readonly Platform[]): string[] {
  return Array.from(new Set(platforms.map((p) => p.category).filter(Boolean))).sort();
}

/** Cuántos filtros (fuera del rango de fechas) están puestos. */
export function activePlatformFilterCount(f: PlatformFilters): number {
  return [f.client, f.category, f.kind, f.model, f.licensing, f.status !== "todas" ? f.status : ""].filter(Boolean).length;
}
